import Image from 'next/image'
import { Carousel } from 'react-responsive-carousel'
import { useState, useCallback, useEffect, useRef } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@icons'
import { useWindowSize, WEBSITE_BREAKPOINTS } from '@platform/utils'
import 'react-responsive-carousel/lib/styles/carousel.min.css' // requires a loader

import CircleButton from '../circle-button'

import styles from './index.module.scss'

const FIRST_ITEM_INDEX = 0
const CONTAINER_PADDING = 16
// Magic number
const WIDTH_ADJUSTMENT = 30

type CarouselCardProps = {
  testimonials: {
    text: string
    author: string
    authorPosition: string
  }[]
  picture: string
}

export const CarouselCard = ({ testimonials, picture }: CarouselCardProps) => {
  const [currentSlide, setCurrentSlide] = useState(FIRST_ITEM_INDEX)
  const [width, setWidth] = useState<number | undefined>(undefined)

  const containerRef = useRef(null)
  const { windowWidth } = useWindowSize()

  const onNextClick = useCallback(() => {
    setCurrentSlide(prevItem => {
      if (prevItem === testimonials.length - 1) {
        return FIRST_ITEM_INDEX
      }
      return prevItem + 1
    })
  }, [testimonials.length])

  const onPreviousClick = useCallback(() => {
    setCurrentSlide(prevItem => {
      if (prevItem === FIRST_ITEM_INDEX) {
        return testimonials.length - 1
      }
      return prevItem - 1
    })
  }, [testimonials.length])

  const onChange = useCallback((currentIndex: number) => {
    setCurrentSlide(currentIndex)
  }, [])

  useEffect(() => {
    if (containerRef.current && windowWidth) {
      if (windowWidth < WEBSITE_BREAKPOINTS.SMALL) {
        setWidth(
          windowWidth -
            2 *
              parseInt(
                window.getComputedStyle(containerRef.current).getPropertyValue('padding'),
                10
              ) -
            2 * CONTAINER_PADDING -
            WIDTH_ADJUSTMENT
        )
        return
      }
      setWidth(undefined)
    }
  }, [windowWidth])

  return (
    <div className={styles['wrapper']}>
      <div className={styles['content']} ref={containerRef}>
        <Carousel
          autoPlay={false}
          selectedItem={currentSlide}
          showArrows={false}
          showStatus={false}
          ariaLabel="Talent testimonials"
          showIndicators={false}
          showThumbs={false}
          onChange={onChange}
          className={styles['carousel']}
          infiniteLoop={true}
          width={width}
        >
          {testimonials.map(item => (
            <div key={item.author} className={styles['testimonial']}>
              <p className={styles['text']}>“{item.text}”</p>
              <p className={styles['author']}>{item.author}</p>
              <p className={styles['author-position']}>{item.authorPosition}</p>
            </div>
          ))}
        </Carousel>
      </div>
      <div className={styles['arrows-container']}>
        <CircleButton
          onClick={onPreviousClick}
          icon={ChevronLeftIcon}
          label="Previous item"
          variant="secondary"
          className={styles['left']}
        />
        <CircleButton
          onClick={onNextClick}
          icon={ChevronRightIcon}
          label="Next item"
          variant="secondary"
          className={styles['right']}
        />
      </div>
      <div className={styles['image-wrapper']}>
        <Image src={picture} alt="Carousel" width="443" height="418" className={styles['image']} />
      </div>
      <div className={styles['image-wrapper-desktop']}>
        <Image
          src={picture}
          alt="Carousel"
          className={styles['image-desktop']}
          fill
          sizes="50rem"
        />
      </div>
    </div>
  )
}

export default CarouselCard
